import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { enterPartnerContext } from '../lib/partnerContext';
import Sidebar from '../components/Sidebar';
import ClientNotificationsTab from '../components/ClientNotificationsTab';

type TabKey = 'overview' | 'notifications';

interface GateResult {
  phase: string;
  gateType: string;
  overallResult: string;
  evaluatedAt: string;
}

interface ProjectDetail {
  id: string;
  name: string;
  currentPhase: string;
  updatedAt: string;
  createdAt: string;
  artefactCount: number;
  gateHealth: 'PASSING' | 'FAILING' | 'NO_GATES';
  latestGates: GateResult[];
}

interface ActivityItem {
  id: string;
  type: 'gate_evaluation' | 'artefact_update';
  subject: string;
  entityId: string;
  timestamp: string;
}

interface ClientDetail {
  clientTenantId: string;
  tenant: { name: string };
  deliveryModel: string;
  linkedAt: string;
  projects: ProjectDetail[];
  recentActivity: ActivityItem[];
}

const GATE_HEALTH_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  PASSING:  { bg: 'rgba(34,197,94,0.1)',  text: '#22c55e', label: 'Passing' },
  FAILING:  { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b', label: 'Failing' },
  NO_GATES: { bg: 'rgba(107,114,128,0.1)', text: '#6b7280', label: 'No Gates' },
};

// Sprint 11 UX.A1: a regex like /^phase_\d+_/ strips only the first digit
// segment, so `phase_2_5_ux_design` would render as "5 Ux Design". Use an
// explicit map; the title-cased fallback covers any future phase that lands
// on this page before the map is updated.
const PHASE_LABEL: Record<string, string> = {
  phase_0_bootstrap:        'Bootstrap',
  phase_1_requirements:     'Requirements',
  phase_2_architecture:     'Architecture',
  phase_2_5_ux_design:      'UX Design',
  phase_3_sprint_planning:  'Sprint Planning',
  phase_4_development:      'Development',
  phase_5_code_review:      'Code Review',
  phase_6_testing:          'Testing',
  phase_7_deployment:       'Deployment',
  phase_8_retrospective:    'Retrospective',
};

function formatPhase(phase: string): string {
  return PHASE_LABEL[phase] ?? phase.replace(/^phase_[\d_]+_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, setSession } = useAuth();
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  useEffect(() => {
    if (!id) return;
    apiFetch<ClientDetail>(`/partner/clients/${id}/detail`)
      .then(setDetail)
      .catch(err => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [id]);

  const handleSwitchContext = async () => {
    if (!id) return;
    const result = await apiFetch<{ contextToken: string; tenantId: string; partnerAccountId: string }>(
      `/partner/clients/${id}/switch`,
      { method: 'POST' },
    );
    if (!user) return;
    // #1330: sync the scoped token through AuthContext (setSession) so
    // useAuth().token tracks the client context without a full page reload.
    enterPartnerContext(
      { contextToken: result.contextToken, partnerAccountId: result.partnerAccountId, returnPath: `/partner/clients/${id}` },
      user,
      setSession,
    );
    navigate('/dashboard');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <main className="flex-1 p-6" style={{ backgroundColor: 'var(--bg)' }}>
        <div className="max-w-6xl mx-auto">
          {/* Back nav */}
          <button
            onClick={() => navigate('/partner/clients')}
            className="text-sm mb-4 hover:underline"
            style={{ color: 'var(--primary)' }}
          >
            &larr; Back to Client Management
          </button>

          {isLoading && <p style={{ color: 'var(--text-muted)' }}>Loading client details...</p>}

          {error && (
            <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
              {error}
            </div>
          )}

          {!isLoading && detail && (
            <>
              {/* Client Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
                    {detail.tenant.name}
                  </h1>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-muted)', color: 'var(--text-muted)' }}>
                      {detail.deliveryModel === 'MANAGED' ? 'Managed' : 'Self-Service'}
                    </span>
                    <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      Linked {new Date(detail.linkedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleSwitchContext}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  Open Client Dashboard
                </button>
              </div>

              {/* Tab nav */}
              <div
                role="tablist"
                className="flex gap-1 mb-4 border-b"
                style={{ borderColor: 'var(--border)' }}
              >
                {([
                  { key: 'overview' as const, label: 'Overview' },
                  { key: 'notifications' as const, label: 'Notifications' },
                ]).map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.key)}
                      data-testid={`client-tab-${tab.key}`}
                      className="px-4 py-2 text-sm font-medium"
                      style={{
                        color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                        borderBottom: isActive ? '2px solid var(--primary)' : '2px solid transparent',
                        marginBottom: '-1px',
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === 'notifications' && (
                <ClientNotificationsTab clientTenantId={detail.clientTenantId} />
              )}

              {activeTab === 'overview' && (
              <div className="flex gap-6">
                {/* Projects */}
                <div className="flex-1">
                  <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text)' }}>
                    Projects ({detail.projects.length})
                  </h2>
                  {detail.projects.length === 0 ? (
                    <div className="border border-dashed rounded-lg p-8 text-center" style={{ borderColor: 'var(--border)' }}>
                      <p style={{ color: 'var(--text-muted)' }}>No active projects</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {detail.projects.map(project => {
                        const health = GATE_HEALTH_STYLES[project.gateHealth] ?? GATE_HEALTH_STYLES.NO_GATES;
                        return (
                          <div
                            key={project.id}
                            className="border rounded-lg p-4"
                            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}
                            data-testid="project-card"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <span className="font-semibold" style={{ color: 'var(--text)' }}>{project.name}</span>
                                <span
                                  className="ml-2 text-xs px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: health.bg, color: health.text }}
                                  data-testid="project-health-badge"
                                >
                                  {health.label}
                                </span>
                              </div>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {timeAgo(project.updatedAt)}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                              <span>Phase: {formatPhase(project.currentPhase)}</span>
                              <span>{project.artefactCount} artefact{project.artefactCount !== 1 ? 's' : ''}</span>
                            </div>
                            {project.latestGates.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {project.latestGates.slice(0, 3).map((gate, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-xs">
                                    <span style={{ color: gate.overallResult === 'FAIL' ? '#f59e0b' : '#22c55e' }}>
                                      {gate.overallResult === 'FAIL' ? 'FAIL' : 'PASS'}
                                    </span>
                                    <span style={{ color: 'var(--text-muted)' }}>
                                      {formatPhase(gate.phase)} {gate.gateType.toLowerCase()} gate
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Recent Activity */}
                <div className="w-80 flex-shrink-0">
                  <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text)' }}>
                    Recent Activity
                  </h2>
                  <div
                    className="border rounded-lg"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}
                    data-testid="activity-panel"
                  >
                    {detail.recentActivity.length === 0 ? (
                      <p className="p-4 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
                        No recent activity
                      </p>
                    ) : (
                      <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                        {detail.recentActivity.slice(0, 15).map(item => (
                          <div key={item.id} className="p-3">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-medium" style={{ color: 'var(--primary)' }}>
                                {item.type === 'gate_evaluation' ? 'Gate' : 'Artefact'}
                              </span>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {timeAgo(item.timestamp)}
                              </span>
                            </div>
                            <p className="text-xs" style={{ color: 'var(--text)' }}>
                              {item.subject}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
